import { Header, Toolbar } from "components/layout/index";
import { useQuery, useMutation } from "react-query";
import SearchBar from "components/search/SearchBar";
import SearchResult from "components/result/SearchResult";
import { useApi } from "hooks/api";
import { FilterProvider } from "hooks/filter";
import { Loader, Notification } from "components/ui";
import { ContentCategory } from "types/appConfigV2";
import { useEffect } from "react";
import browserFingerprint from "utilities/browserHash";
import { useSessionDetails } from "hooks/sessionDetails";
import { MediaModalProvider } from "hooks/mediaModal";
import type { PropertyCriteria } from 'types/qbank/propertyCriteria';
import type { SearchOperator } from 'types/qbank/searchOperator';
import { PropertyType } from "types/qbank/propertyType";

export default function Content() {
  const { setSessionId, setSessionDetails } = useSessionDetails();
  const { getRemoteOverride, getCurrentUser, session, getPropertSet } = useApi();
  const { mutate: sessionMutation } = useMutation(session, {
    onSuccess: (data) => {
      setSessionId(data);
    },
  });

  useEffect(() => {
    sessionMutation({
      eventSession: {
        sourceId: 1,
        sessionHash: browserFingerprint() + " | " + Date.now().toString(),
        remoteIp: "0.0.0.0",
        userAgent:
          navigator && navigator.userAgent
            ? navigator.userAgent
            : "Connector V2 userAgent",
        userId: 1,
      },
    });
  }, [sessionMutation]);

  const currentUser = useQuery("getCurrentUser", getCurrentUser, {
    staleTime: Infinity,
    retry: false,
    onSuccess: (data) => {
      const { userName, email } = data;
      setSessionDetails({ username: userName, email });
    },
  });
  const { data: propertySet, isLoading: dataIsLoading, isError: dataIsError } = useQuery("getPropertySet", getPropertSet, { staleTime: Infinity, retry: false });

  const { isLoading, isError, data } = useQuery(
    "remoteOverride",
    getRemoteOverride,
    { staleTime: Infinity, retry: false }
  );

  if (isLoading || currentUser.isLoading) {
    return <Loader />;
  }

  if (isError || !data) {
    return <Notification>Misconfigured, check config!</Notification>;
  }

  //transformObject function to return an array of PropertyCriteria
  function getSystemName(propertyData: PropertyType[] | undefined, id: number): string {
    if (!propertyData) throw new Error('propertyData empty.');
    const value = propertyData.find(item => item.id === id)?.systemName;
    if (!value) { throw new Error('item not found on propertyData'); }
    else { return value; }
  }

  function isArrary(propertyData: PropertyType[] | undefined, id: number): boolean {
    if (!propertyData) throw new Error('propertyData empty.');
    const value = propertyData.find(item => item.id === id)?.definition.array;
    if (value && typeof value === 'boolean') {
      return value; // if definition has value
    }
    else {
      return false;
    }
  }

  function transformObject(originalObject: { [key: string]: { operator: string, value: string }[] }): PropertyCriteria[] {
    try {

      const transformedArray: PropertyCriteria[] = [];
      if (!dataIsLoading && !dataIsError) {
        for (const key in originalObject) {

          if (Object.prototype.hasOwnProperty.call(originalObject, key)) {
            const id = parseInt(key);
            const operators = originalObject[key];
            const systemname = getSystemName(propertySet, id);
            const isArray = isArrary(propertySet, id);

            operators.forEach(({ operator, value }) => {
              let transformedValue: any = value;
              if (isArray) {
                transformedValue = value.split(',').map((v: any) => v.trim()); // convert comma seperated value to a list/array
              }
              transformedArray.push({
                id: id,
                systemName: systemname,
                value: transformedValue,
                operator: operator as SearchOperator
              });
            });
          }
        }
      }

      return transformedArray;
    } catch (error) {
      // Handle any potential errors here
      console.error('An error occurred during object transformation propertyCriteria to Property list :', error);
      return [];
    }
  }


  //Transforming propertyCriteria in to Property list 
  Object.entries(data.categories).map(
    ([key, value]) => {

      var propertyCriteria = value.filter?.propertyCriteria;
      if (propertyCriteria) {

        var transformedProperties = transformObject(propertyCriteria);
        if (transformedProperties.length > 0) {
          value.filter.properties = transformedProperties;
        }
      }
      return { key: key, value: value };
    }
  );

  const activeCategory = Object.entries(
    data.categories
  )[0][1] as ContentCategory;

  return (
    <FilterProvider initialCategory={activeCategory}>
      <MediaModalProvider initialState={{ open: false }}>
        <Header categories={data.categories} />
        <SearchBar />
        <Toolbar />
        <SearchResult />
      </MediaModalProvider>
    </FilterProvider>
  );
}
